"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { useEditorBundle, useBundleRoot } from "@/hooks/use-editor-bundle";
import { useAuth } from "@/lib/auth";
import { useHeaderAction } from "@/lib/header-action-context";
import { useCapabilityActions } from "@/hooks/use-capability-actions";
import { useCapabilityUpdate } from "@/hooks/use-entity-mutations";
import { CapabilityDetailLayout, WORKING_COPY_ID } from "@/components/capability-detail/CapabilityDetailLayout";
import type { DeferredOverviewDirtyPayload } from "@/components/capability-detail/CapabilityOverviewTab";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
   AlertDialog,
   AlertDialogAction,
   AlertDialogCancel,
   AlertDialogContent,
   AlertDialogDescription,
   AlertDialogFooter,
   AlertDialogHeader,
   AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Pencil, X, Plug } from "lucide-react";
import { CapabilityHeaderActions } from "@/components/capability-detail/CapabilityHeaderActions";
import { capabilityColors } from "@/lib/capability-colors";
import { api } from "@/lib/api-client";
import { toast } from "sonner";
import {
   parametersFromRecord,
   parametersToRecord,
   type ModelParameterItem,
} from "@/components/models/ModelParameterEditor";
import { ModelDetailView } from "@/components/models/ModelDetailView";
import { ProviderPickerDrawer } from "@/components/models/ProviderPickerDrawer";
import { ProviderApiKeys } from "@/components/models/ProviderApiKeys";
import { logger } from "@/lib/telemetry";
import { cn } from "@/lib/utils";

const LOG_PREFIX = "model-detail-page";
const HEADER_LAYER_ID = "model-page";

/** Provider card content - exact copy of ProviderPickerDrawer card structure */
function ProviderCardContent({
   providerRef,
   appKey,
   providers,
   bundle,
   onRemove,
   canRemove = true,
}: {
   providerRef: string;
   appKey: string;
   providers: ProviderOption[];
   bundle: { localRefs?: Record<string, Record<string, unknown>> } | undefined;
   onRemove: () => void;
   canRemove?: boolean;
}) {
   const fromList = providers.find(
      (x) =>
         x.name === providerRef ||
         (x.app_key && `${x.app_key}/${x.name}` === providerRef)
   );
   const refId = providerRef.includes("/") ? providerRef : appKey ? `${appKey}/${providerRef}` : null;
   const fromBundle = refId && bundle?.localRefs?.[refId];
   const descFromBundle =
      fromBundle && typeof fromBundle === "object" && !Array.isArray(fromBundle)
         ? (fromBundle as Record<string, unknown>).description
         : undefined;
   const description =
      (fromList?.description?.trim() as string | undefined) ??
      (typeof descFromBundle === "string" ? descFromBundle : null);
   const displayName =
      fromList?.name ?? (providerRef.includes("/") ? providerRef.split("/").pop() : providerRef);

   return (
      <Card
         className={cn(
            "relative border-2 p-3 transition-all duration-200 min-h-[88px]",
            "border-l-4 border-slate-200 bg-white hover:shadow-md"
         )}
         style={{ borderLeftColor: capabilityColors.provider.hex }}
      >
         <div className="flex flex-row items-start gap-3">
            <div
               className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
                  capabilityColors.provider.bgLight,
                  capabilityColors.provider.text
               )}
            >
               <Plug size={16} />
            </div>
            <div className="flex flex-col flex-1 min-w-0 gap-0.5">
               <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                  provider
               </span>
               <span className="text-sm font-bold truncate text-slate-900">
                  {displayName}
               </span>
               {description && (
                  <span className="text-[10px] text-slate-500 line-clamp-2 leading-tight">
                     {description}
                  </span>
               )}
            </div>
            {canRemove && (
               <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 shrink-0 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50"
                  onClick={(e) => {
                     e.stopPropagation();
                     onRemove();
                  }}
                  aria-label="Remove provider"
               >
                  <X size={10} />
               </Button>
            )}
         </div>
      </Card>
   );
}

interface ProviderOption {
   name: string;
   description: string | null;
   tags?: string[];
   app_key?: string;
   visibility?: string;
   created_by?: string;
   organization_id?: string;
}

interface ModelCapabilityData {
   id?: string;
   name?: string;
   description?: string | null;
   tags?: string[];
   type?: string;
   config?: {
      provider_ref?: string;
      providerRef?: string;
      provider?: string;
      model_id?: string;
      modelId?: string;
      model?: string;
      parameters?: Record<string, unknown>;
      parameter_meta?: Record<
         string,
         { type?: string; min?: number; max?: number }
      >;
   };
   working_revision?: {
      type_config?: {
         provider_ref?: string;
         providerRef?: string;
         provider?: string;
         model_id?: string;
         modelId?: string;
         model?: string;
         parameters?: Record<string, unknown>;
      };
   };
}

function extractModelManifestState(data: ModelCapabilityData | undefined) {
   if (!data) {
      return {
         provider_ref: "",
         model_id: "",
         parameters: [] as ModelParameterItem[],
      };
   }
   const config = data.config ?? {};
   const manifest = data.working_revision?.type_config ?? {};
   const provider_ref =
      (config.provider_ref as string) ??
      (config.providerRef as string) ??
      (config.provider as string) ??
      (manifest.provider_ref as string) ??
      (manifest.providerRef as string) ??
      (manifest.provider as string) ??
      "";
   const model_id =
      (config.model_id as string) ??
      (config.modelId as string) ??
      (config.model as string) ??
      (manifest.model_id as string) ??
      (manifest.modelId as string) ??
      (manifest.model as string) ??
      "";
   const paramsRecord =
      (config.parameters as Record<string, unknown>) ??
      (manifest.parameters as Record<string, unknown>) ??
      {};
   const paramMeta = (config.parameter_meta as Record<
      string,
      { type?: string; min?: number; max?: number }
   >) ?? {};
   const metaTyped = Object.fromEntries(
      Object.entries(paramMeta).map(([k, v]) => [
         k,
         {
            type: v?.type as ModelParameterItem["type"] | undefined,
            min: v?.min,
            max: v?.max,
         },
      ])
   );
   const parameters = parametersFromRecord(paramsRecord, metaTyped);

   return {
      provider_ref,
      model_id,
      parameters,
   };
}

export interface ModelDetailPageProps {
   appKey?: string;
   name?: string;
   /** When provided, CapabilityDetailLayout pushes center + versionSelector to this header layer. */
   headerLayerId?: string;
}

export default function ModelDetailPage({ appKey: appKeyProp, name: nameProp, headerLayerId }: ModelDetailPageProps = {}) {
   const params = useParams<{ appKey: string; name: string }>();
   const appKey = appKeyProp ?? params.appKey;
   const name = nameProp ?? params.name;
   const { identity, currentOrganization } = useAuth();
   const { data: bundle, isLoading, refetchWithoutCache } = useEditorBundle(appKey!, name!);
   const data = useBundleRoot(bundle);
   const updateMutation = useCapabilityUpdate(appKey!, name!);
   const { pushHeaderLayer, removeHeaderLayer } = useHeaderAction();
   const status = (data as { status?: string })?.status ?? "";
   const isArchived = status.toLowerCase() === "archived";

   const [formState, setFormState] = useState({
      provider_ref: "",
      model_id: "",
      parameters: [] as ModelParameterItem[],
   });
   const [providers, setProviders] = useState<ProviderOption[]>([]);
   const [loadingProviders, setLoadingProviders] = useState(false);
   const [isProviderDrawerOpen, setIsProviderDrawerOpen] = useState(false);
   const [overviewPatch, setOverviewPatch] = useState<Record<string, unknown>>({});
   const [selectedRevisionId, setSelectedRevisionId] = useState(WORKING_COPY_ID);
   const isViewingPublished = selectedRevisionId !== WORKING_COPY_ID;

   const handleOverviewDeferredDirtyChange = useCallback((payload: DeferredOverviewDirtyPayload) => {
      setOverviewPatch((prev) => {
         const next = payload.patch;
         if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
         return next;
      });
   }, []);

   const formReadOnly = isArchived || !(data as Record<string, unknown> | undefined)?.working_revision || isViewingPublished;

   const modelData = data as ModelCapabilityData | undefined;

   const immutableRevisions = useMemo(
      () => ((data as Record<string, unknown>)?.immutable_revisions ?? []) as Array<{
         id: string; type_config?: Record<string, unknown>;
      }>,
      [data],
   );

   const selectedImmutableRevision = useMemo(
      () => immutableRevisions.find((r) => r.id === selectedRevisionId),
      [immutableRevisions, selectedRevisionId],
   );

   useEffect(() => {
      setLoadingProviders(true);
      api.get("/capabilities", { params: { type: "provider", include_entitled: "true" } })
         .then((res) => {
            const payload = res.data;
            const items = (
               Array.isArray(payload)
                  ? payload
                  : Array.isArray(payload?.data)
                     ? payload.data
                     : []
            ) as Array<{
               name: string;
               description: string | null;
               tags?: string[];
               app_key?: string;
               visibility?: string;
               created_by?: string;
               organization_id?: string;
            }>;
            setProviders(
               items.map((p) => ({
                  name: p.name,
                  description: p.description,
                  tags: Array.isArray(p.tags) ? p.tags : [],
                  app_key: p.app_key,
                  visibility: p.visibility,
                  created_by: p.created_by,
                  organization_id: p.organization_id,
               }))
            );
         })
         .catch(() => setProviders([]))
         .finally(() => setLoadingProviders(false));
   }, []);

   useEffect(() => {
      if (isViewingPublished && selectedImmutableRevision) {
         const tc = selectedImmutableRevision.type_config ?? {};
         const provider_ref = (tc.provider_ref as string) ?? (tc.providerRef as string) ?? (tc.provider as string) ?? "";
         const model_id = (tc.model_id as string) ?? (tc.modelId as string) ?? (tc.model as string) ?? "";
         const paramsRecord = (tc.parameters as Record<string, unknown>) ?? {};
         const parameters = parametersFromRecord(paramsRecord, {});
         setFormState({ provider_ref, model_id, parameters });
         return;
      }
      const extracted = extractModelManifestState(modelData);
      setFormState((prev) => ({
         provider_ref: extracted.provider_ref,
         model_id: extracted.model_id,
         parameters:
            extracted.parameters.length > 0
               ? extracted.parameters
               : prev.parameters.length > 0
                  ? prev.parameters
                  : [],
      }));
   }, [modelData?.config, modelData?.working_revision?.type_config, isViewingPublished, selectedImmutableRevision]);

   const extractedSource = useMemo(
      () => extractModelManifestState(modelData),
      [modelData?.config, modelData?.working_revision?.type_config]
   );
   const isDirty = useMemo(
      () =>
         formState.provider_ref !== extractedSource.provider_ref ||
         formState.model_id !== extractedSource.model_id ||
         JSON.stringify(formState.parameters) !== JSON.stringify(extractedSource.parameters),
      [formState, extractedSource]
   );

   const overviewDirty = Object.keys(overviewPatch).length > 0;
   const anyDirty = isDirty || overviewDirty;

   const handleSave = useCallback(() => {
      if (!appKey || !name) return;
      if (!isDirty && !overviewDirty) return;
      const payload: Record<string, unknown> = {};
      if (overviewDirty) {
         Object.assign(payload, overviewPatch);
      }
      if (isDirty) {
         const { parameters, parameterMeta } = parametersToRecord(formState.parameters);
         payload.type_config = {
            provider_ref: formState.provider_ref || null,
            model_id: formState.model_id || null,
            parameters,
            parameter_meta: parameterMeta,
         };
      }
      updateMutation.mutate(payload, {
         onSuccess: () => {
            setOverviewPatch({});
            toast.success("Model updated");
            logger.info(`${LOG_PREFIX} handleSave: model updated`, {
               provider_ref: formState.provider_ref,
               model_id: formState.model_id,
            });
         },
         onError: (err) => {
            logger.error(`${LOG_PREFIX} handleSave: update failed`, {
               error: err,
            });
            toast.error("Failed to update model");
         },
      });
   }, [
      appKey,
      name,
      isDirty,
      overviewDirty,
      overviewPatch,
      formState.provider_ref,
      formState.model_id,
      formState.parameters,
      updateMutation,
   ]);

   const actions = useCapabilityActions({
      appKey: appKey!,
      name: name!,
      typeLabel: "Model",
      data: data as Record<string, unknown> | null,
      refetch: refetchWithoutCache,
      customSave: handleSave,
      customIsDirty: anyDirty,
      customIsSaving: updateMutation.isPending,
   });

   const saveRef = useRef(actions.save);
   saveRef.current = actions.save;
   const publishRef = useRef(actions.publish);
   publishRef.current = actions.publish;
   const editRef = useRef(actions.edit);
   editRef.current = actions.edit;

   const effectiveLayerId = headerLayerId ?? HEADER_LAYER_ID;

   useEffect(() => {
      if (!data || isArchived) {
         removeHeaderLayer(effectiveLayerId);
         return;
      }
      pushHeaderLayer(effectiveLayerId, {
         action: (
            <CapabilityHeaderActions
               typeLabel="Model"
               hasWorkingRevision={actions.hasWorkingRevision}
               isViewingPublished={actions.isViewingPublished}
               isArchived={isArchived}
               selectedPublishedLabel={actions.selectedPublishedLabel}
               isDirty={actions.isDirty}
               isSaving={actions.isSaving}
               isPublishing={actions.isPublishing}
               onSave={() => void saveRef.current()}
               onPublish={() => void publishRef.current()}
               onEdit={() => editRef.current()}
               onArchive={() => actions.openArchiveDialog()}
               onSettings={() => actions.openSettings()}
            />
         ),
      });
      return () => removeHeaderLayer(effectiveLayerId);
   }, [
      data,
      isArchived,
      effectiveLayerId,
      actions.hasWorkingRevision,
      actions.isViewingPublished,
      actions.selectedPublishedLabel,
      actions.isDirty,
      actions.isSaving,
      actions.isPublishing,
      pushHeaderLayer,
      removeHeaderLayer,
   ]);

   const providerSlot = (
      <div
         id="model-provider"
         role="button"
         tabIndex={formReadOnly ? -1 : 0}
         onClick={() => {
            if (!formReadOnly) setIsProviderDrawerOpen(true);
         }}
         onKeyDown={(e) => {
            if (formReadOnly) return;
            if (e.key === "Enter" || e.key === " ") {
               e.preventDefault();
               setIsProviderDrawerOpen(true);
            }
         }}
         className={cn(!formReadOnly && "cursor-pointer")}
      >
         {formState.provider_ref ? (
            <ProviderCardContent
               providerRef={formState.provider_ref}
               appKey={appKey ?? ""}
               providers={providers}
               bundle={bundle}
               canRemove={!formReadOnly}
               onRemove={() => {
                  setFormState((prev) => ({
                     ...prev,
                     provider_ref: "",
                  }));
               }}
            />
         ) : (
            <Card
               className={cn(
                  "border-2 border-dashed border-slate-200 bg-slate-50/50 p-3",
                  formReadOnly && "pointer-events-none opacity-60"
               )}
            >
               <div className="flex flex-col items-center justify-center py-2">
                  <span className="mb-1 text-green-400">
                     <Plug size={24} />
                  </span>
                  <p className="text-xs font-medium text-green-600">
                     {formReadOnly ? "No provider" : "Click to select provider"}
                  </p>
                  {loadingProviders && !formReadOnly && (
                     <p className="text-xs text-slate-400 mt-1">Loading providers...</p>
                  )}
                  <p className="text-xs text-slate-500 mt-2 italic">
                     The provider capability that handles LLM API calls for this model.
                  </p>
               </div>
            </Card>
         )}
      </div>
   );

   const parametersTab = (
      <ModelDetailView
         modelId={formState.model_id}
         parameters={formState.parameters}
         readOnly={formReadOnly}
         onModelIdChange={(value) =>
            setFormState((prev) => ({ ...prev, model_id: value }))
         }
         onParametersChange={(parameters) =>
            setFormState((prev) => ({ ...prev, parameters }))
         }
         providerSlot={providerSlot}
      />
   );

   if (isLoading) {
      return (
         <div className="flex items-center justify-center p-12">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
         </div>
      );
   }

   if (!data) {
      return (
         <div className="p-6">
            <p className="text-slate-600">Model not found.</p>
         </div>
      );
   }

   return (
      <>
         <CapabilityDetailLayout
            typeLabel="Model"
            appKey={appKey}
            capabilityName={name}
            selectedRevisionId={selectedRevisionId}
            onRevisionChange={setSelectedRevisionId}
            onOverviewDeferredDirtyChange={handleOverviewDeferredDirtyChange}
            pendingOverviewPatch={overviewPatch}
            onSaveDeferred={() => void handleSave()}
            isOverviewDirty={anyDirty}
            isSavingOverview={updateMutation.isPending}
            overviewFormDisabled={updateMutation.isPending || !actions.hasWorkingRevision || isViewingPublished}
            headerLayerId={headerLayerId}
         >
            <Card className="border-white/30 bg-white/40 p-6 shadow-sm">
               {parametersTab}
               {formState.provider_ref && (
                  <div className="mt-6 **:data-[slot=card]:border-slate-200 **:data-[slot=card]:dark:border-slate-700">
                     <ProviderApiKeys
                        providerRef={formState.provider_ref}
                        appKey={appKey ?? ""}
                     />
                  </div>
               )}
            </Card>
         </CapabilityDetailLayout>
         <AlertDialog open={actions.replaceDialogOpen} onOpenChange={actions.setReplaceDialogOpen}>
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                     <Pencil size={18} className="text-amber-600" />
                     Replace working copy?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                     A working copy already exists. Replacing it will discard any unsaved changes
                     and create a new working copy based on the selected published version.
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={actions.handleConfirmReplace}>
                     Replace
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>
         <ProviderPickerDrawer
            isOpen={isProviderDrawerOpen && !formReadOnly}
            onClose={() => setIsProviderDrawerOpen(false)}
            providers={providers}
            selectedProviderRef={formState.provider_ref}
            onSelect={(providerName) =>
               setFormState((prev) => ({
                  ...prev,
                  provider_ref: providerName,
               }))
            }
            loading={loadingProviders}
            currentUserId={identity?.sub}
            currentOrgId={currentOrganization?.id}
         />
      </>
   );
}
